/**
 * Shared IndexedDB for Dog Salon Snaps.
 */

const DB_NAME = "dog-salon-snaps";
const DB_VERSION = 7;

export const VISIT_STORE = "visits";
export const STICKER_STORE = "customStickers";
export const PET_STORE = "pets";
export const RESERVATION_STORE = "reservations";
export const INQUIRY_STORE = "ownerInquiries";
export const LEDGER_STORE = "opsLedger";
/** Accepted owner custom item proposals (album-saved only). */
export const CUSTOM_PROPOSAL_STORE = "customProposals";

export function openAppDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;
      if (!db.objectStoreNames.contains(VISIT_STORE)) {
        const store = db.createObjectStore(VISIT_STORE, { keyPath: "id" });
        store.createIndex("shotDate", "shotDate", { unique: false });
        store.createIndex("petName", "petName", { unique: false });
        store.createIndex("breed", "breed", { unique: false });
        store.createIndex("seasonPackId", "seasonPackId", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
        store.createIndex("petId", "petId", { unique: false });
      } else if (oldVersion < 3) {
        const tx = req.transaction;
        const store = tx?.objectStore(VISIT_STORE);
        if (store && !store.indexNames.contains("petId")) {
          store.createIndex("petId", "petId", { unique: false });
        }
      }
      if (!db.objectStoreNames.contains(STICKER_STORE)) {
        const stickers = db.createObjectStore(STICKER_STORE, { keyPath: "id" });
        stickers.createIndex("seasonPackId", "seasonPackId", { unique: false });
        stickers.createIndex("updatedAt", "updatedAt", { unique: false });
      }
      if (!db.objectStoreNames.contains(PET_STORE)) {
        const pets = db.createObjectStore(PET_STORE, { keyPath: "id" });
        pets.createIndex("name", "name", { unique: false });
        pets.createIndex("updatedAt", "updatedAt", { unique: false });
      }
      if (!db.objectStoreNames.contains(RESERVATION_STORE)) {
        const reservations = db.createObjectStore(RESERVATION_STORE, { keyPath: "id" });
        reservations.createIndex("date", "date", { unique: false });
        reservations.createIndex("petId", "petId", { unique: false });
        reservations.createIndex("status", "status", { unique: false });
      }
      if (!db.objectStoreNames.contains(INQUIRY_STORE)) {
        const inquiries = db.createObjectStore(INQUIRY_STORE, { keyPath: "id" });
        inquiries.createIndex("createdAt", "createdAt", { unique: false });
        inquiries.createIndex("topic", "topic", { unique: false });
      }
      if (!db.objectStoreNames.contains(LEDGER_STORE)) {
        const ledger = db.createObjectStore(LEDGER_STORE, { keyPath: "id" });
        ledger.createIndex("date", "date", { unique: false });
        ledger.createIndex("kind", "kind", { unique: false });
        ledger.createIndex("cutRecordId", "cutRecordId", { unique: false });
      }
      if (!db.objectStoreNames.contains(CUSTOM_PROPOSAL_STORE)) {
        const proposals = db.createObjectStore(CUSTOM_PROPOSAL_STORE, {
          keyPath: "id",
        });
        proposals.createIndex("lastUsedAt", "lastUsedAt", { unique: false });
        proposals.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
}

export function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}
